import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState, StatusBadge, ConfirmDialog, Badge } from "@/components/common";
import { IntervalToggle, planPrice } from "@/pages/public/Pricing";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { t } from "@/i18n";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

function StatusLine({ sub, tz }) {
  if (sub.billing_mode === "manual" && sub.paid_until) return t("p7.billing.paidUntil", { date: fmtDate(sub.paid_until, tz) });
  if (sub.status === "trial" && sub.trial_ends_at) return `${t("p7.billing.trialLeft", { n: daysLeft(sub.trial_ends_at) })} · ${t("p7.billing.trialEnds", { date: fmtDate(sub.trial_ends_at, tz) })}`;
  if (sub.current_period_end) return t(sub.cancel_at_period_end ? "p7.billing.endsOn" : "p7.billing.renews", { date: fmtDate(sub.current_period_end, tz) });
  return null;
}

function Notice({ sub, cancelledCheckout }) {
  let key = null;
  if (sub.awaiting_card) key = "awaitingCard";
  else if (sub.status === "suspended" && sub.lapse_reason === "trial_expired") key = "expired";
  else if (sub.status === "cancelled") key = "cancelled";
  return (
    <>
      {cancelledCheckout && <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="checkout-cancelled-note">{t("p7.billing.checkoutCancelled")}</p>}
      {key && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800" data-testid={`billing-notice-${key}`}>{t(`p7.billing.${key}`)}</p>}
    </>
  );
}

function PlanOption({ plan, interval, current, hasSub, onPick, busy }) {
  const price = planPrice(plan, interval);
  return (
    <div className={`flex flex-col rounded-xl border p-5 ${current ? "border-[#0A2540] ring-1 ring-[#0A2540]" : "border-slate-200"}`} data-testid={`billing-plan-${plan.name.toLowerCase()}`}>
      <div className="flex items-center justify-between"><p className="font-display text-xl font-bold">{plan.name}</p>{current && <Badge tone="navy">{t("p7.billing.current")}</Badge>}</div>
      <p className="mt-2 text-sm text-slate-600">{price ? t("p7.billing.pricePer", { price: fmtMoney(price), per: t(interval === "yearly" ? "p7.plans.perYear" : "p7.plans.perMonth") }) : t("p7.pricing.notOnInterval")}</p>
      <p className="mt-1 text-xs text-slate-500">{t("p7.pricing.users", { n: plan.max_internal_users })}</p>
      <Button className="mt-4" disabled={!price || current || busy} onClick={() => onPick(plan)} data-testid={`billing-subscribe-${plan.name.toLowerCase()}`}>
        <CreditCard className="mr-2 h-4 w-4" />{hasSub ? t("p7.billing.change") : t("p7.billing.subscribe")}
      </Button>
    </div>
  );
}

export function SubscriptionPanel() {
  const { garage, access, reload: reloadGarage } = useGarage();
  const [params] = useSearchParams();
  const { data, error, loading, reload } = useFetch("/garage/subscription");
  const [interval, setInterval] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const sub = data.subscription;
  const iv = interval || sub.interval || "monthly";
  const live = data.has_stripe_subscription && ["active", "trialing", "past_due"].includes(sub.stripe_status);
  const canAct = access.role === "garage_manager";

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      reload();
      reloadGarage();
      return res;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = (plan) => act(async () => {
    const { data: r } = await api.post("/garage/subscription/checkout", { plan_id: plan.id, interval: iv, origin_url: window.location.origin });
    if (r.checkout_url) window.location.href = r.checkout_url;
    else toast.success(t("p7.billing.changed"));
  });
  const portal = () => act(async () => { window.location.href = (await api.post("/garage/subscription/portal", { origin_url: window.location.origin })).data.url; });

  return (
    <div className="space-y-6" data-testid="subscription-panel">
      <Notice sub={sub} cancelledCheckout={params.get("checkout") === "cancelled"} />
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("p7.billing.title")}</p>
          <p className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="billing-plan-name">{data.plan?.name || "—"} <span className="text-sm font-normal text-slate-500">· {t(`p7.pricing.${sub.interval || "monthly"}`)}</span></p>
          <p className="mt-1 text-sm text-slate-600" data-testid="billing-status-line">{StatusLine({ sub, tz: garage.timezone })}</p>
          <p className="mt-1 text-xs text-slate-500">{t("p7.billing.mode")}: {sub.billing_mode === "manual" ? t("p7.billing.manual") : t("p7.billing.stripe")}</p>
        </div>
        <StatusBadge status={sub.status} testId="billing-status-badge" />
      </div>
      {canAct && (
        <div className="flex flex-wrap gap-2">
          {data.has_billing_account && <Button variant="outline" disabled={busy} onClick={portal} data-testid="billing-portal-button"><ExternalLink className="mr-2 h-4 w-4" />{t("p7.billing.portal")}</Button>}
          {live && !sub.cancel_at_period_end && <Button variant="ghost" className="text-red-600" disabled={busy} onClick={() => setConfirmCancel(true)} data-testid="billing-cancel-button">{t("p7.billing.cancel")}</Button>}
          {live && sub.cancel_at_period_end && <Button variant="outline" disabled={busy} onClick={() => act(() => api.post("/garage/subscription/resume"), t("p7.billing.resumed"))} data-testid="billing-resume-button">{t("p7.billing.resume")}</Button>}
        </div>
      )}
      {canAct && (
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><p className="text-sm font-semibold text-slate-800">{t("p7.billing.choose")}</p><IntervalToggle value={iv} onChange={setInterval} testId="billing-interval" /></div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.plans.map((p) => <PlanOption key={p.id} plan={p} interval={iv} busy={busy} hasSub={live} current={live && p.id === sub.plan_id && iv === sub.interval} onPick={pick} />)}
          </div>
        </div>
      )}
      <p className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4" />{t("p7.billing.secure")}</p>
      {data.stripe_mode === "test" && <p className="text-xs text-amber-700" data-testid="billing-test-mode-note">{t("p7.billing.testMode")}</p>}
      <ConfirmDialog open={confirmCancel} onOpenChange={setConfirmCancel} destructive busy={busy} title={t("p7.billing.cancelTitle")}
        description={t("p7.billing.cancelText", { date: sub.current_period_end ? fmtDate(sub.current_period_end, garage.timezone) : "—" })}
        confirmLabel={t("p7.billing.cancel")} onConfirm={() => act(() => api.post("/garage/subscription/cancel"), t("p7.billing.cancelled_ok")).then(() => setConfirmCancel(false))} testId="billing-cancel-dialog" />
    </div>
  );
}
